// --------------------------------------------------------
// HMV Questionnaire - HMVQuestionnaire
// --------------------------------------------------------

// --------------------------------------------------------
// IMPORTS
// --------------------------------------------------------
import React, { useCallback, useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import { useAppDispatch } from "../..";
import { GenericSolidButton } from "../../components/UI/buttons/Button.styled";
import { GeneralContainerQuestionnaire, Text } from "../../components/UI/generalLayout/Layout.styled";
import ScreenHeader from "../../components/UI/screenHeader/ScreenHeader";
import Colors from "../../constants/Colors";
import { PersonService } from "../../core/http/service/PersonService";
import { Paths } from "../../enums/Paths";
import {
  getUniqueListBy,
  handleBackendErrors,
} from "../../helpers/HelperFunctions";
import useResponsiveInfo from "../../hooks/useResponsiveInfo";
import BaseResponse from "../../models/core/BaseResponse";
import AvaliacaoResposta from "../../models/core/questionnaire/AvaliacaoResposta";
import Enunciado from "../../models/core/questionnaire/Enunciado";
import * as questionnaireActions from "../../store/actions/questionnaire";
import * as loginActions from "../../store/actions/login";
import * as personActions from "../../store/actions/person";
import HMVQuestion from "./HMVQuestion";
import { SaveButton } from "./HMVQuestionnaire.styled";
import Resposta from "../../models/core/questionnaire/Resposta";
import { Alert } from "@mui/material";
import HeaderLogount from "../../components/headerLogount/HeaderLogount";
import ContainerWithSidebar from "../../components/UI/containerWithSidebar/ContainerWithSidebar";
import QuestionnaireHeader from "../../components/UI/header/QuestionnaireHeader";

// --------------------------------------------------------
// HMV Questionnaire INTERFACE
// --------------------------------------------------------
/**
 * A component that renders a Screen.
 *
 * @param props Properties
 * @param props.route The route
 * @param props.navigation The navigator
 * @returns The component JSX.
 */
function HMVQuestionnaire(): JSX.Element {
  // --------------------------------------------------------
  // HMV Questionnaire PRIVATE DECLARATIONS
  // --------------------------------------------------------
  const { isMobile } = useResponsiveInfo();
  const [allAnswers, setAllAnswers] = useState([]);
  const [activeQuestionsNumber, setActiveQuestionsNumber] = useState(0);
  const [isFinalizarDisabled, setIsFinalizarDisabled] = useState(true);
  const [flagFetchAvaliacao, setFlagFetchAvaliacao] = useState(false);
  const [textAnswers, setTextAnswers] = useState<any[]>([]);
  const [hasAnyAnswerError, setHasAnyAnswerError] = useState(false);
  const [questionFlow, setQuestionFlow] = useState<any[]>([]);
  const [hiddenIndexes, setHiddenIndexes] = useState<number[] | null>(null);
  const [generalErrors, setGeneralErrors] = useState<any[]>([]);
  const [avaliacaoState, setAvaliacaoState] = useState<any>(null);
  const [successMessage, setSuccessMessage] = useState<string>("");
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { state }: any = useLocation();
  const { questionarioHMV } = useSelector((state: any) => state.questionnaire);
  const { pessoa } = useSelector((state: any) => state.person);
  const { userInfo } = useSelector((state: any) => state.login);
  const { isView, pessoaResposta, avaliacaoId } = !!state ? state : { isView: false, pessoaResposta: null, avaliacaoId: null };

  const getQuestionario = useCallback(async () => {
    await dispatch(questionnaireActions.fetchQuestionarioHMV());
    await dispatch(personActions.fetchPerson(userInfo.id));
  }, [dispatch]);

  useEffect(() => {
    getQuestionario();
  }, [getQuestionario]);

  const fetchAvaliacao = useCallback(
    async (avaliacaoId: string, pessoaId: string) => {
      const service: PersonService = new PersonService();

      const response: any = await service.getAvaliacaoById(
        avaliacaoId,
        pessoaId
      );
      const baseResponse: BaseResponse<any> = new BaseResponse().fromJSON(
        response.data
      );

      const avaliacaoRetorno = new AvaliacaoResposta().fromJSON({
        ...baseResponse.data,
      });
      setAvaliacaoState(avaliacaoRetorno);
    },
    []
  );

  useEffect(() => {
    if (questionarioHMV !== null) {
      if(!isView){
        const questionarioList = userInfo.questionarios.filter((q: any) => {
          return q.questionarioId === questionarioHMV.id;
        });
        const avaliacaoRespondida = questionarioList[0].avaliacaoRespondida;
        if (avaliacaoRespondida !== null) {
          fetchAvaliacao(avaliacaoRespondida.avaliacaoId, userInfo.id);
        } else {
          setAvaliacaoState(null);
        }
      }else{
        fetchAvaliacao(avaliacaoId, pessoaResposta.idPessoa);
      }
    }
  }, [questionarioHMV]);

  useEffect(() => {
    if (avaliacaoState !== null) {
      if (!isView && avaliacaoState.finalizada) {
        navigate(Paths.CollaboratorInitial); //Posteriormente será p/ home
      } else {
        questionarioHMV.enunciados.forEach((e: any) => {
          if (e.tipoDadoResposta.codigo === "lista" && e.maximo === null) {
            const respostaLista = avaliacaoState.respostas.filter(
              (r: Resposta) => {
                return r.perguntaId === e.pergunta.id;
              }
            );
            if (respostaLista.length > 0) {
              const resposta = respostaLista[0];
              resposta.alternativas.forEach((alt: any) => {
                const alternativaIn = {
                  entradaTexto: alt.texto !== "",
                  respostaTexto: alt.texto,
                  id: alt.alternativaId,
                };
                setAllAnswers((prev: any) => {
                  const lista: any = !!prev ? [...prev] : [];
                  const index = lista.findIndex((el: any) => {
                    return el.id === alt.alternativaId;
                  });
                  index === -1 &&
                    lista.push({
                      questionId: e.pergunta.id,
                      ordemPergunta: e.ordem,
                      ...alternativaIn,
                    });
                  return lista;
                });

                if (alt.texto !== "") {
                  setTextAnswers((prev: any) => {
                    const list = [...prev];
                    const index = prev.findIndex((el: any) => {
                      return el.id === alt.alternativaId;
                    });
                    index === -1 &&
                      list.push({
                        questionId: e.pergunta.id,
                        ordemPergunta: e.ordem,
                        ...alternativaIn,
                      });

                    return list;
                  });
                }
              });
            }
          }
        });
      }
    }
  }, [avaliacaoState]);

  useEffect(() => {
    const hiddenIndexesNumber: number[] = [];
    if (questionarioHMV !== null) {
      questionarioHMV.enunciados.forEach((e: any) => {
        const nextQuestionList = questionarioHMV.fluxo.filter((f: any) => {
          return f.perguntaOrigemId === e.pergunta.id;
        });
        const nextQuestion: any =
          nextQuestionList.length > 0 ? nextQuestionList[0] : null;
        if (nextQuestion != null) {
          const nextEnunciado: any[] = questionarioHMV.enunciados.filter(
            (e: any) => {
              return e.pergunta.id === nextQuestion.perguntaDestinoId;
            }
          );

          for (var i = e.ordem + 1; i < nextEnunciado[0].ordem; i++) {
            hiddenIndexesNumber.push(i);
          }
        }
      });
      setHiddenIndexes(hiddenIndexesNumber);
      setQuestionFlow(questionarioHMV.fluxo);
    }
  }, [questionarioHMV]);

  useEffect(() => {
    if (questionarioHMV !== null && questionarioHMV.enunciados != null) {
      setActiveQuestionsNumber(
        questionarioHMV.enunciados.length - (hiddenIndexes?.length ?? 0)
      );
    }
  }, [questionarioHMV, hiddenIndexes]);

  useEffect(() => {
    const list = [...allAnswers, ...textAnswers];
    const activeQuestionsList = list.filter((el) => {
      return !hiddenIndexes?.includes(el.ordemPergunta);
    });

    const uniqueQuestionsAnswered: any[] = Array.from(
      getUniqueListBy(activeQuestionsList, "questionId")
    );
    if (uniqueQuestionsAnswered.length === activeQuestionsNumber) {
      setIsFinalizarDisabled(false);
    } else {
      setIsFinalizarDisabled(true);
    }
  }, [activeQuestionsNumber, allAnswers, textAnswers]);

  const updateHiddenIndexes = (
    ordem: number,
    questionId: string,
    answerId: string
  ) => {
    const newHiddenIndexes: number[] = [];
    let retorno: number[] = [];

    const flowList = questionFlow.filter((f: any) => {
      return f.perguntaOrigemId === questionId;
    });

    const flow = flowList.length > 0 ? flowList[0] : null;
    if (flow !== null) {
      const nextEnunciado: any[] = questionarioHMV.enunciados.filter(
        (e: any) => {
          return e.pergunta.id === flow.perguntaDestinoId;
        }
      );

      for (var i = ordem + 1; i < nextEnunciado[0].ordem; i++) {
        newHiddenIndexes.push(i);
      }

      if (answerId === flow.alternativaOrigemId) {
        newHiddenIndexes.forEach((i) => {
          if (!hiddenIndexes?.includes(i)) {
            retorno.push(i);
          }
        });
        setHiddenIndexes([...(hiddenIndexes ?? []), ...retorno]);
      } else {
        retorno =
          hiddenIndexes?.filter((i) => {
            return !newHiddenIndexes.includes(i);
          }) ?? [];
        setHiddenIndexes(retorno);
      }
    }
  };

  const isMultipleChoice = (enunciado: Enunciado) => {
    return enunciado.maximo === null;
  };

  const answerQuestionnaire = async (isSalvar: boolean) => {
    try {
      const enunciadosAbertos: any[] = questionarioHMV.enunciados.filter(
        (enunciado: any) => {
          return !hiddenIndexes?.includes(enunciado.ordem);
        }
      );
      const idAvaliacao = avaliacaoState === null ? null : avaliacaoState.id;
      await dispatch(
        questionnaireActions.registerQuestionarioHMV(
          isSalvar,
          enunciadosAbertos,
          allAnswers,
          textAnswers,
          questionarioHMV.id ?? "",
          questionarioHMV.versao ?? 1,
          idAvaliacao,
          pessoa.id
        )
      );

      if (!isSalvar) {
        navigate(Paths.CollaboratorInitial); //Posteriormente mudar para Home
        await dispatch(loginActions.updateUserInfo());
      } else {
        window.scrollTo({ top: 0, left: 0 });
      }
    } catch (error: any) {
      handleBackendErrors(error, setGeneralErrors);
    }
  };

  const ButtonArea = () => {
    return <>
      {!isView && (<div
        style={{
          marginTop: 32,
          marginBottom: 32,
          flexDirection: "row",
          display: "flex",
        }}
      >
        <SaveButton
          disabled={hasAnyAnswerError}
          style={{ marginRight: 12 }}
          onClick={() => {
            answerQuestionnaire(true);
            setSuccessMessage("Questionário salvo com sucesso!");
          }}
        >
          Salvar respostas
        </SaveButton>
        <GenericSolidButton
          disabled={isFinalizarDisabled || hasAnyAnswerError}
          color={Colors.SPM600}
          onClick={() => {
            answerQuestionnaire(false);
          }}
        >
          Finalizar questionário
        </GenericSolidButton>
      </div>)}</>
  }

  return (
    <>
      {!isView && (<HeaderLogount />)}
      {isView && (<ContainerWithSidebar>
        {questionarioHMV !== null &&
          questionFlow !== null &&
          hiddenIndexes !== null && (
            <div>
              <QuestionnaireHeader pessoa={pessoaResposta} />
              {questionarioHMV.enunciados.map((e: any, index: number) => {
                const isPerguntaEscondida = hiddenIndexes.includes(e.ordem);
                return (
                  <>
                    {!isPerguntaEscondida && (
                      <HMVQuestion
                        isViewState={isView}
                        respostas={
                          avaliacaoState !== null
                            ? avaliacaoState.respostas
                            : []
                        }
                        setButtonDisabled={setHasAnyAnswerError}
                        key={index}
                        enunciado={e}
                        showCheckbox={isMultipleChoice(e)}
                        onChangeCheckOrRadioValue={updateHiddenIndexes}
                        onChangeTextValue={() => { }}
                        setAllAnswers={setAllAnswers}
                        setTextAnswers={setTextAnswers}
                      />
                    )}
                  </>
                );
              })}
              <ButtonArea />
            </div>
          )}
      </ ContainerWithSidebar>)}
      {!isView && (<GeneralContainerQuestionnaire>
        {questionarioHMV !== null &&
          questionFlow !== null &&
          hiddenIndexes !== null && (
            <div
              style={{
                marginLeft: isMobile ? 0 : 300,
                marginRight: isMobile ? 0 : 300,
              }}
            >
              <ScreenHeader
                screenTitle={"Questionário de Saúde - HMV"}
                rightButtonText={""}
                hasRightButton={false}
                onRightButtonClick={() => { }}
                errors={generalErrors}
                onCloseError={() => {
                  setGeneralErrors([]);
                }}
              />
              {successMessage !== "" && (
                <div style={{ marginBottom: 20 }}>
                  <Alert
                    severity="success"
                    onClose={() => {
                      setSuccessMessage("");
                    }}
                  >
                    {successMessage}
                  </Alert>
                </div>
              )}

              {questionarioHMV.enunciados.map((e: any, index: number) => {
                const isPerguntaEscondida = hiddenIndexes.includes(e.ordem);
                return (
                  <>
                    {!isPerguntaEscondida && (
                      <HMVQuestion
                        isViewState={isView}
                        respostas={
                          avaliacaoState !== null
                            ? avaliacaoState.respostas
                            : []
                        }
                        setButtonDisabled={setHasAnyAnswerError}
                        key={index}
                        enunciado={e}
                        showCheckbox={isMultipleChoice(e)}
                        onChangeCheckOrRadioValue={updateHiddenIndexes}
                        onChangeTextValue={() => { }}
                        setAllAnswers={setAllAnswers}
                        setTextAnswers={setTextAnswers}
                      />
                    )}
                  </>
                );
              })}

              <ButtonArea />

            </div>
          )}
      </GeneralContainerQuestionnaire>)}
    </>
  );
}

export default HMVQuestionnaire;
